import React, { useRef } from 'react';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import UserForm from './UserForm';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import UpgradeSeatsForm from '../../views/settings/billing/UpgradeSeatsForm';
import { useGlobalContext } from 'GlobalContext';
import { UserType } from './UserTypes';
import WarningModal from 'components/modals/WarningModal';
import { saveData } from 'services/api/saveData';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';

// Rename the name of the function:
const UserList: React.FC = () => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { environmentType, userHash } = useAuthContext();
    const isSuperuser = useAllowedRight('no_user');
    const refetchListRef = useRef<() => void>();

    // Trials and the internal admin and beta environments add users without a seat limit
    const hasUnlimitedSeats = ['trial', 'admin', 'beta'].includes(environmentType);

    // Determine whether the seat limit of the environment is reached
    const isSeatLimitReached = (metaData: Record<string, any> | null) => {
        if (!metaData || hasUnlimitedSeats || metaData.has_unlimited_seats || metaData.is_trial !== false) {
            return false;
        }
        return metaData.active_seats >= metaData.total_seats;
    };

    // Filter the list on the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionParams = !isEnvironmentSelected && selectedDivision ? { 'division_scope': selectedDivision.id } : {};

    // Specify the options of this list:
    const query = {
        endpoint: 'get_user_list',
        defaultParams: { 'is_active': true, 'exclude_subcontractors': true, ...divisionParams },
        object: 'user'
    }
    const objectName = 'user.general.object_name'
    const showSearch = false
    const showDeleteOrDeactivate = 'flag_deactivated'
    
    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <UserForm />;

    // Handle user row click
    const handleUserRowClick = (row: UserType) => {

        // Superuser accounts can only be opened by themselves, staff accounts also by superusers
        const isSelf = row.user_hash === userHash;
        if (row.is_superuser && !isSelf) return;
        if (row.is_staff && !isSelf && !isSuperuser) return;

        // When the user is demo data, show a warning modal
        if (row.is_demo_data && row.is_active) {
            initializeModal(
                <WarningModal
                    type="info"
                    icon={faTriangleExclamation}
                    header="user.general.demo_user_title"
                    message="user.general.demo_user_message"
                    buttonLabel="user.general.deactivate_user"
                    onApproval={async () => {
                        await saveData({
                            apiUrl: `patch_user/${row.id}`,
                            method: 'patch',
                            data: { is_active: false }
                        })

                        if (refetchListRef.current) {
                            refetchListRef.current()
                        }
                    }}
                />,
                { modalSize: 'extra-small' }
            )

            return
        };
    
        // Get the existing item
        const itemId = row.id;

        // Open the edit modal for existing items, the row tells the form whether the user can become a subcontractor
        initializeModal(
            React.cloneElement(formModal, { itemId, user: row }),
            {
                title: t('button.edit_object_label', { object_name: t(`${objectName}.singular`) }),
                itemId,
                modalSize: 'small',
                onSuccess: () => { if (refetchListRef.current) refetchListRef.current() }
            }
        )
    }

    // Handle the two factor reset of a user
    const handleTwoFactorReset = (event: React.MouseEvent, row: UserType) => {

        // Prevent opening the user edit modal from the row click
        event.stopPropagation();

        // Show a warning modal before resetting the two factor authentication
        initializeModal(
            <WarningModal
                type="info"
                icon={faTriangleExclamation}
                header="user.general.two_factor_reset_label"
                message="user.general.two_factor_reset_message"
                buttonLabel="user.general.two_factor_reset_button_label"
                onApproval={async () => {
                    await saveData({
                        apiUrl: `reset_user_two_factor/${row.id}`,
                        method: 'post',
                        data: {}
                    })

                    setFloatingAlert({ type: 'success' })

                    if (refetchListRef.current) {
                        refetchListRef.current()
                    }
                }}
            />,
            { modalSize: 'extra-small' }
        )
    }

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { width: '2fr', field: 'full_name', label: 'user.general.full_name_label' },
        { width: '2fr', field: 'email', label: 'user.general.username_label' },
        { width: '2fr', field: 'role_name', label: 'user.general.role_label' },
        {
            width: '2fr',
            field: 'two_factor_confirmed',
            label: 'user.general.two_factor_label',
            requiredFeature: 'enhanced_security',
            disableOrdering: true,
            render: (value, item) => (
                // Show the two factor status with a reset link when it is active
                value === true ? (
                    <span>
                        {t('user.general.two_factor_active_label')}
                        <span> • </span>
                        <span className='text-link'
                              onClick={(event) => handleTwoFactorReset(event, item as unknown as UserType)}>
                            {t('user.general.two_factor_reset_button_label')}
                        </span>
                    </span>
                ) : (
                    <span>{t('user.general.two_factor_inactive_label')}</span>
                )
            )
        },
    ];

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'user.general.active_users_label',
            params: { 'is_active': true, 'exclude_subcontractors': true }
        },
        {
            id: 'inactive',
            label: 'user.general.inactive_users_label',
            params: { 'is_active': false, 'exclude_subcontractors': true }
        }
    ];

    // Handle the opening of the manage seats modal
    const handleOpenManageSeatsModal = () => {
        initializeModal(
            <UpgradeSeatsForm />, {
                title: t('billing.general.manage_seats_header'),
                modalSize: 'small-medium',
                onSuccess: () => {if (refetchListRef.current) refetchListRef.current()}
            }
        );
    };

    // Render the seats info
    const renderSeatsInfo = (metaData: Record<string, any> | null) => {
        // If no meta data is provided, return null
        if (!metaData) {
            return null;
        }

        // Get the seats info from the meta data
        const { active_seats, total_seats, is_trial, has_unlimited_seats } = metaData;

        // Return the seats info, without seats management for unlimited environments
        return (
            <div className="text">
                {is_trial || has_unlimited_seats || hasUnlimitedSeats ? (
                    <span>{active_seats} {t('billing.general.seats_in_use')}</span>
                ) : (
                    <>
                        <span>{active_seats}/{total_seats} {t('billing.general.seats_in_use')}</span>
                        <span> • </span>
                        <span className='text-link'
                              onClick={() => {handleOpenManageSeatsModal()}}>
                            {t('billing.general.add_seats')}
                        </span>
                    </>
                )}
            </div>
        );
    };

    // Configure the dynamic form modal
    const getDynamicModal = (metaData: Record<string, any> | null) => {

        // When the active seats is equal to the total seats, return upgrade seats form
        if (isSeatLimitReached(metaData)) {
            return <UpgradeSeatsForm />;
        }

        // Otherwise return the add user form
        return <UserForm />;
    };

    // Set the modal size based on the dynamic modal
    const getDynamicModalSize = (metaData: Record<string, any> | null): 'small' | 'small-medium' | 'medium' | 'large' | 'xtra-large' => {

        // If the manage seats modal is shown, set modal size to small-medium
        if (isSeatLimitReached(metaData)) {
            return 'small-medium';
        }

        // Otherwise set as small
        return 'small';
    };

    // Configure the dynamic form modal title
    const getDynamicModalTitle = (metaData: Record<string, any> | null) => {

        // When the active seats is equal to the total seats, return upgrade seats form
        if (isSeatLimitReached(metaData)) {
            return t('billing.general.manage_seats_header');
        }

        // Otherwise return the add user form
        return t('button.add_object_label', { object_name: t(`${objectName}.singular`) });
    };

    return (
        <AbstractList
            key={!isEnvironmentSelected && selectedDivision ? selectedDivision.id : 'environment'}
            query={query}
            objectName={objectName}
            filters={filters}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            isPaginated={isPaginated}
            onRowClick={handleUserRowClick}
            renderHeaderInfo={renderSeatsInfo}
            getFormModal={getDynamicModal}
            getFormModalTitle={getDynamicModalTitle}
            getModalSize={getDynamicModalSize}
            setRefetchList={(fn) => { refetchListRef.current = fn; }}
        />
    );
};

export default UserList;